import {getProvenanceDisplay} from './sourcesProvenance';

/**
 * Build a stable, unique localId for a federated hit so Save/Cite identity does not
 * collapse across results.
 *
 *  - corpus_segment  → canonical `docId:idx:off:idx:off` (parsable downstream) when the
 *    segment index is known, otherwise `c:{docId}:{segmentId}`.
 *  - data_item_reference → `d:{referenceId|canonicalFileId}:{pmDocumentId}:{pmFrom}`.
 *  - report_segment  → `r:{reportId}:{segmentId|chunkIndex}`.
 *
 * Falls back to a content-derived id only as a last resort so two distinct hits never
 * share an id (which previously made Save no-op for every card but the first).
 */
function buildStableLocalId(hit, target, {docId, index} = {}) {
    const kind = target?.kind;
    const offset = target?.segmentOffset ?? 0;

    if (kind === 'corpus_segment') {
        if (docId != null && index != null) {
            return [docId, index, offset, index, offset].join(':');
        }
        if (target?.docId != null) {
            return `c:${target.docId}:${target.segmentId ?? target.segmentIndex ?? 'x'}`;
        }
    }

    if (kind === 'data_item_reference') {
        const fileKey = target?.referenceId ?? target?.canonicalFileId;
        const range = hit?.sentenceHit?.range || {};
        const pmDoc = hit?.sentenceHit?.pmDocumentId ?? range.pmDocumentId ?? 'x';
        const pmFrom = range.pmFrom ?? 'x';
        if (fileKey != null) {
            return `d:${fileKey}:${pmDoc}:${pmFrom}`;
        }
    }

    if (kind === 'report_segment') {
        const reportId = target?.reportId;
        const seg = target?.segmentId ?? target?.chunkIndex;
        if (reportId != null && seg != null) {
            return `r:${reportId}:${seg}`;
        }
    }

    if (kind === 'highlight') {
        // Keyed by the matched region (the addressable unit of a highlight hit) so two
        // matched regions of the same highlight keep distinct Save/Cite identity.
        const regionKey = target?.highlightRegionId ?? target?.highlightId;
        if (regionKey != null) {
            return `h:${regionKey}`;
        }
    }

    // Last-resort: namespace by store + any available identifiers + a short content hash
    // so two different snippets from the same doc still differ.
    const store = hit?.documentStore || 's';
    const idPart = target?.docId ?? target?.canonicalFileId ?? target?.reportId ?? 'x';
    const snippet = typeof hit?.snippet === 'string' ? hit.snippet : '';
    let h = 0;
    for (let i = 0; i < snippet.length; i += 1) {
        h = (h * 31 + snippet.charCodeAt(i)) | 0;
    }
    return `${store}:${idPart}:${h}`;
}

/**
 * Map a federated {@code UnifiedSearchHit} (corpus / data_item / report) onto the flat prop
 * shape consumed by the shared {@code SegmentItem} card so every store renders with the same
 * rich UI (content + highlight, existing tags, suggested tags, classification chips, Save,
 * Cite this, comments).
 *
 * Identity fields (docId/segmentId/index/offset) only exist for corpus segments; for
 * data_item and report hits they are derived where possible and otherwise left null. The
 * SegmentItem card degrades gracefully: Save/Cite remain inert when {@code computeLocalId}
 * cannot resolve a stable localId, while content, tags and provenance still render.
 */
export function unifiedHitToSegmentProps(hit, {query} = {}) {
    if (!hit) return null;

    const target = hit.openTarget || {};
    const ownership = hit.ownership || {};
    const provenance = getProvenanceDisplay(hit);
    const isCorpus = (hit.documentStore === 'corpus') || (target.kind === 'corpus_segment');

    // docId / segmentId / index identify a corpus segment and key the tag + Save/Cite
    // machinery. Reports and data items live in different id spaces, so we deliberately leave
    // these null for non-corpus hits to avoid cross-store id collisions (tag suggestions,
    // votes and Save/Cite then no-op gracefully while content + provenance still render).
    const docId = isCorpus ? (target.docId ?? null) : null;
    const segmentId = isCorpus ? (target.segmentId ?? null) : null;
    const index = isCorpus ? (target.segmentIndex ?? null) : null;

    // Source-type sentinel: machine-readable constant that ReferenceListItem uses to
    // route the viewer and render the correct type chip. Derived from openTarget.kind
    // so it is always accurate regardless of what provenance labels the API returns.
    //   data_item_reference → 'MY_DATA'
    //   report_segment      → 'REPORT'
    //   corpus_segment / default → 'CORPUS'
    const docSourceType = (() => {
        const kind = target?.kind;
        if (kind === 'data_item_reference') return 'MY_DATA';
        if (kind === 'report_segment') return 'REPORT';
        if (kind === 'highlight') return 'HIGHLIGHT';
        return 'CORPUS';
    })();

    const docSource = ownership.docSource || provenance.secondary || null;

    const isHighlightHit = target.kind === 'highlight';
    // hit.highlightedExcerpt carries the highlight's own saved text for highlight hits (see
    // HighlightSearchAdapter); hit.title is the owning *document's* name there, not the excerpt.
    // Falls back to the context snippet when the saved region had no text of its own (e.g. an
    // untexted FULL_SEGMENT bookmark).
    const highlightExcerpt = hit.highlightedExcerpt || hit.snippet || '';

    // Every federated hit needs a stable, unique localId so Save/Cite dedupe per-result
    // instead of collapsing all non-corpus cards onto a single null id (which made Save
    // appear to work only for the first/corpus card). Corpus keeps the canonical
    // `docId:idx:off:idx:off` id so existing open/dedupe logic that parses it still works;
    // data_item and report use opaque namespaced ids (their open path uses openTarget, not
    // localId parsing).
    const stableLocalId = buildStableLocalId(hit, target, {docId, index});

    return {
        docId,
        // Explicit localId drives Save/Cite identity across all stores (addItem accepts it).
        localId: stableLocalId,
        // For data_item_reference hits: the file ID to pass to the Sources file viewer.
        // docId is intentionally null for non-corpus hits; fileId carries the correct
        // referenceId / canonicalFileId so ReferenceListItem can open the viewer.
        // Highlight hits open the bookmarked data item too, keyed by its canonical file id.
        fileId: target.kind === 'data_item_reference'
            ? (target.referenceId != null ? String(target.referenceId)
                : (target.canonicalFileId != null ? String(target.canonicalFileId) : null))
            : (target.kind === 'highlight' && target.canonicalFileId != null
                ? String(target.canonicalFileId)
                : null),
        // For report_segment hits: the report ID to pass to the report viewer.
        // docId is null for non-corpus hits, so reportId is stored separately.
        reportId: target.kind === 'report_segment'
            ? (target.reportId != null ? String(target.reportId) : null)
            : null,
        // Report product type (e.g. analytic / iir) — cite gate allows IIR only.
        productType: target.kind === 'report_segment'
            ? (hit.productType != null ? String(hit.productType) : null)
            : null,
        // Store-specific tag identifiers — each store keys tags in its own table, so the
        // card's tagging hook (useSearchResultTags) routes by docSourceType + these ids:
        //   REPORT    → reportSegmentId (report_segments.segment_id, entity_tags votes)
        //   MY_DATA   → dataItemSegmentId (data_item_segments.id, DiTag toggle)
        //   HIGHLIGHT → highlightDataItemId (bookmark DataItem id, DiTag toggle)
        reportSegmentId: target.kind === 'report_segment' ? (target.segmentId ?? null) : null,
        dataItemSegmentId: target.kind === 'data_item_reference' ? (target.dataItemSegmentId ?? null) : null,
        highlightDataItemId: target.kind === 'highlight' ? (target.canonicalFileId ?? null) : null,
        // Raw document type (PDF, AUDIO, VIDEO, IMAGE, etc.) — used to derive the
        // fileType bucket for the file viewer and to show a type chip in the citation card.
        documentType: hit.documentType || null,
        docTitle: hit.title || 'Untitled',
        docSource,
        docSourceType,
        docClassificationLevel: ownership.classificationLevel || hit.classificationLevel || null,
        // Highlight hits show ONLY the highlighted excerpt as the result content — showing the
        // full surrounding paragraph made it unclear which part actually matched. (Opening the
        // result still shows the full document for context; see useOpenSearchHit.) saveContent
        // mirrors content so Save/Cite/drag persist exactly what's displayed.
        content: isHighlightHit ? highlightExcerpt : (hit.snippet || ''),
        saveContent: isHighlightHit ? highlightExcerpt : (hit.snippet || ''),
        // Entity/highlight spans are offsets into the full context snippet, which highlight hits
        // no longer display as `content` — drop them rather than mis-marking the excerpt text.
        entities: isHighlightHit ? [] : (hit.entities || []),
        segmentId,
        index,
        offset: target.segmentOffset ?? 0,
        tags: hit.tags || undefined,
        existingTags: hit.existingTags || undefined,
        newTags: hit.newTags || undefined,
        segmentType: 'search',
        query: query || '',
        // Normalized relevance score in [0,1] from the federated ranker; surfaced to admins
        // in-card to help debug ranking. May be undefined for legacy/non-scored payloads.
        score: typeof hit.score === 'number' ? hit.score : undefined,
        // Store-relative date (ISO-8601 string from the API): corpus = publish date,
        // data item = created date, report = approval date (proxied by updated_at). Null
        // when the source row has no date; the card hides the chip in that case.
        documentDate: hit.documentDate || null,
        // Federated source provenance ({kind, primary, secondary}) so the card can render an
        // in-card source badge (Corpus / My file / Report) alongside its metadata chips.
        provenance,
        // carry the original hit so the open handler can branch on openTarget.kind
        __unifiedHit: hit,
    };
}
